import { useContext, useState, useMemo, useCallback } from 'react';
import {
    CButton,
    CCard,
    CCardBody,
    CForm,
    CCol,
} from '@coreui/react';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { CustomExternalDataBody, CustomExternalDataContext } from '.';
import {
    API_CLIENT_EXTERNAL_DATA_CUSTOM,
    API_CLIENT_EXTERNAL_UPDATE_DATA,
    CUSTOM_EXTERNAL_DATA_MODAL_STEPS,
    SUBSCRIBER_PATH,
} from '../../../../../../constants';
import { callTokenApi } from '../../../../../../apiCaller';
import { toastError } from '../../../../../../utils';
import Waiting from '../../../../../general/Loadings/Waiting';
import { HoverInfoIconTooltip } from '../../../../../general/custom-tooltips';
import { useHistory } from 'react-router-dom';
import { CInput, CTextarea } from '../../../../../migration/CInput';
import { CInvalidFeedback } from '../../../../../migration/CInvalidFeedback';
import CLabel from '../../../../../migration/CLabel';
import CFormGroup from '../../../../../migration/CFormGroup';
import { setAccountExternalDataSources, setAccountExternalDataSourcesCustom, setRoutes } from '../../../../../../actions/subscriber';
import { getExternalDataRoutes } from '../../../../../../helpers/cms/subscriber';

const getValidationSchema = () =>
    Yup.object().shape({
        name: Yup.string()
            .trim()
            .required('Required.')
            .matches(/^[a-zA-Z0-9 ]+$/, 'Allow only alphabet, numeric and spacing characters.'),
    });

const NameExternalData = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { setCurrentStep, closeModal } = useContext(CustomExternalDataContext);

    const { activeAccount, customExternalDataModal, routes, accountDestinations, accountExternalData: sources, accountExternalDataCustom: customSources } = useSelector(
        (state) => ({
            activeAccount: state.subscriber.activeAccount,
            customExternalDataModal: state.subscriber.customExternalDataModal,
            routes: state.subscriber.routes,
            accountDestinations: state.subscriber.accountDestinations,
            accountExternalData: state.subscriber.accountExternalData.sources,
            accountExternalDataCustom: state.subscriber.accountExternalData.customSources,
        })
    );
    const timeZone = activeAccount.timeZone;
    const activeAccountId = activeAccount.id;
    const secondId = activeAccount.secondId;
    const data = customExternalDataModal.data;

    const isUpdating = !!data?.isUpdating;
    const initialValues = useMemo(
        () => ({
            name: data?.name || '',
            description: data?.description || '',
        }),
        [data]
    );

    const handleBack = useCallback(() => {
        if (isUpdating) {
            closeModal();
        } else {
            setCurrentStep(CUSTOM_EXTERNAL_DATA_MODAL_STEPS.INTRO);
        }
    }, [isUpdating, closeModal, setCurrentStep]);

    const handleChangeActiveExternalData = useCallback(
        async (external) => {
            try {
                const data = {
                    accountId: activeAccountId,
                    externalId: external.id,
                    status: true,
                    timeZone,
                };

                const response = await callTokenApi(API_CLIENT_EXTERNAL_UPDATE_DATA, 'POST', data);
                if (response.status === 200) {
                    const newSource = { ...external, accountExternalData: response?.data?.externalAccountData };
                    const customExternalData = getExternalDataRoutes([...(sources || []), newSource].filter(_s => _s.createdAccountId), secondId);

                    dispatch(setAccountExternalDataSources([...(sources || []), newSource]));
                    dispatch(setAccountExternalDataSourcesCustom([...(customSources || []), newSource]));
                    dispatch(setRoutes([...routes, ...customExternalData]));
                    closeModal();
                } else {
                    toastError(response);
                }
            } catch (error) {
                toastError(error);
            }
        },
        [activeAccountId, timeZone, dispatch, closeModal, sources, routes, secondId, customSources]
    );

    const createExternalData = useCallback(
        async (values, { setSubmitting }) => {
            const body = {
                name: values.name,
                description: values.description,
                accountId: activeAccountId,
            };

            try {
                const response = await callTokenApi(API_CLIENT_EXTERNAL_DATA_CUSTOM, 'POST', body);
                if (response.status === 200) {
                    const { external } = response.data;
                    await handleChangeActiveExternalData(external);

                    history.replace(
                        `${SUBSCRIBER_PATH.EXTERNAL_DATA.replace(':secondId', activeAccount.secondId)}/${external.alias}`
                    );
                } else {
                    setSubmitting(false);
                    toastError(response);
                }
            } catch (error) {
                setSubmitting(false);
            }
        },
        [activeAccountId, history, activeAccount.secondId, handleChangeActiveExternalData]
    );

    const editExternalData = useCallback(
        async (values, { setSubmitting }) => {
            const body = {
                accountId: activeAccountId,
                id: data.id,
                name: values.name,
                description: values.description,
            };

            const nameChanged = data.name !== values.name;

            try {
                const response = await callTokenApi(`${API_CLIENT_EXTERNAL_DATA_CUSTOM}/${data.id}`, 'PUT', body);
                if (response.status === 200) {
                    const { external } = response.data;
                    const newAccountDestinations = [...accountDestinations];
                    const idx = newAccountDestinations.findIndex((el) => el.id === data.id);
                    newAccountDestinations[idx] = { ...newAccountDestinations[idx], ...external };

                    const updatedSources = (sources || []).map((source) =>
                        source.id === external.id
                            ? { ...source, ...external }
                            : source
                    );

                    const updatedCustomSources = (customSources || []).map(
                        (source) =>
                            source.id === external.id
                                ? { ...source, ...external }
                                : source
                    );

                    const { accountExternalData } = updatedCustomSources.find((source) => source.id === external.id);
                    const newSource = { ...external, accountExternalData };
                    const customExternalData = getExternalDataRoutes([...(sources || []), newSource].filter(_s => _s.createdAccountId), activeAccount.secondId);

                    dispatch(setAccountExternalDataSources(updatedSources));
                    dispatch(
                        setAccountExternalDataSourcesCustom(updatedCustomSources)
                    );
                    dispatch(setRoutes([...routes, ...customExternalData]));
                   
                    closeModal();

                    if (nameChanged) {
                        history.replace(
                            `${SUBSCRIBER_PATH.EXTERNAL_DATA.replace(':secondId', activeAccount.secondId)}/${external.alias}`
                        );
                    }
                } else {
                    setSubmitting(false);
                    toastError(response);
                }
            } catch (error) {
                setSubmitting(false);
                toastError(error);
            }
        },
        [
            dispatch,
            activeAccountId,
            accountDestinations,
            sources,
            customSources,
            history,
            data,
            closeModal,
            activeAccount.secondId,
            routes
        ]
    );

    const handleSubmit = useCallback(
        (values, actions) => {
            if (isUpdating) {
                editExternalData(values, actions);
            } else {
                createExternalData(values, actions);
            }
        },
        [isUpdating, editExternalData, createExternalData]
    );

    const [validateOnChange, setValidateOnChange] = useState(false);
    const validationSchema = useCallback(() => {
        setValidateOnChange(true);
        return getValidationSchema();
    }, []);

    return (
        <CustomExternalDataBody onBackBtnClicked={handleBack}>
            <CCard className="name-destination">
                <CCardBody>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        validateOnChange={validateOnChange}
                        validateOnBlur={false}
                        enableReinitialize
                    >
                        {({ values, errors, handleChange, handleSubmit, isSubmitting }) => (
                            <CForm noValidate onSubmit={handleSubmit}>
                                <h3>Name Your External Data</h3>
                                <p>Provide a name and description for your external data so you can manage it easily inside ListenLayer.</p>

                                <CFormGroup className="text-left">
                                    <CFormGroup row className="align-items-center">
                                        <CCol md="3" className="d-flex align-items-center">
                                            <CLabel className="name">Name</CLabel>
                                            <div className="tooltip-wrapper">
                                                <HoverInfoIconTooltip>
                                                    <p>We recommend using the name of the destination or something you will easily recognize.</p>
                                                </HoverInfoIconTooltip>
                                            </div>
                                        </CCol>

                                        <CCol md="9">
                                            <CInput
                                                name="name"
                                                invalid={!!errors.name}
                                                value={values.name}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            />
                                            <CInvalidFeedback>{errors.name}</CInvalidFeedback>
                                        </CCol>
                                    </CFormGroup>

                                    <CFormGroup row className="align-items-center">
                                        <CLabel className="col-md-3">Description</CLabel>
                                        <CCol md="9">
                                            <CTextarea
                                                name="description"
                                                invalid={!!errors.description}
                                                value={values.description}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            />
                                            <CInvalidFeedback>{errors.description}</CInvalidFeedback>
                                        </CCol>
                                    </CFormGroup>
                                </CFormGroup>

                                <CButton type="submit" className="btn-save mb-3" disabled={isSubmitting}>
                                    <Waiting isLoading={isSubmitting}>SAVE CHANGES</Waiting>
                                </CButton>

                                {!isUpdating && (
                                    <div>You can edit all settings for your external data after it is created.</div>
                                )}
                            </CForm>
                        )}
                    </Formik>
                </CCardBody>
            </CCard>
        </CustomExternalDataBody>
    );
};

export default NameExternalData;
